import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { toast } from 'sonner';
import {
  Search,
  Plus,
  Edit2,
  X,
  Loader2,
  RefreshCw,
  AlertCircle,
  ChevronDown,
  Save,
  Mail,
  User,
  Shield,
  UserX,
  AlertTriangle,
  AppWindow,
} from 'lucide-react';

const NEXUS_ROLES = [
  { value: 'user', label: 'Utilisateur' },
  { value: 'admin_structure', label: 'Admin Structure' },
  { value: 'super_admin', label: 'Super Admin' },
];

const ROLE_BADGE = {
  super_admin: 'bg-purple-100 text-purple-700',
  admin_structure: 'bg-blue-100 text-blue-700',
  user: 'bg-slate-100 text-slate-600',
};

const STATUS_BADGE = {
  active: 'bg-green-100 text-green-700',
  inactive: 'bg-slate-100 text-slate-500',
  suspended: 'bg-red-100 text-red-700',
};

const Users = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [confirmDeactivate, setConfirmDeactivate] = useState(null);
  const [saving, setSaving] = useState(false);

  const emptyForm = {
    email: '',
    nom: '',
    prenom: '',
    nexus_role: 'user',
    app_roles: {},
  };
  const [form, setForm] = useState(emptyForm);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [usersRes, appsRes] = await Promise.allSettled([
        api.get('/api/users'),
        api.get('/api/apps'),
      ]);
      if (usersRes.status === 'fulfilled') {
        const data = usersRes.value.data;
        setUsers(Array.isArray(data) ? data : data.items || []);
      }
      if (appsRes.status === 'fulfilled') {
        const data = appsRes.value.data;
        setApps(Array.isArray(data) ? data : data.items || []);
      }
    } catch (err) {
      setError('Impossible de charger les utilisateurs');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const filtered = users.filter((u) => {
    const fullName = `${u.nom || u.last_name || ''} ${u.prenom || u.first_name || ''} ${u.email || ''}`.toLowerCase();
    const matchSearch = !search || fullName.includes(search.toLowerCase());
    const matchRole = !roleFilter || (u.nexus_role || u.role) === roleFilter;
    return matchSearch && matchRole;
  });

  const openAddModal = () => {
    setEditingUser(null);
    setForm(emptyForm);
    setModalOpen(true);
  };

  const openEditModal = (u) => {
    setEditingUser(u);
    setForm({
      email: u.email || '',
      nom: u.nom || u.last_name || '',
      prenom: u.prenom || u.first_name || '',
      nexus_role: u.nexus_role || u.role || 'user',
      app_roles: u.app_roles || {},
    });
    setModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingUser) {
        await api.put(`/api/users/${editingUser.id}`, form);
        toast.success('Utilisateur mis a jour');
      } else {
        await api.post('/api/users', form);
        toast.success('Utilisateur cree');
      }
      setModalOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async () => {
    if (!confirmDeactivate) return;
    setSaving(true);
    try {
      await api.delete(`/api/users/${confirmDeactivate.id}`);
      toast.success('Utilisateur desactive');
      setConfirmDeactivate(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la desactivation');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="h-8 bg-slate-100 rounded w-48 animate-pulse" />
          <div className="h-10 bg-slate-100 rounded w-32 animate-pulse" />
        </div>
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="flex items-center gap-4 p-4 border-b border-slate-100 animate-pulse">
              <div className="w-10 h-10 bg-slate-100 rounded-full" />
              <div className="flex-1 space-y-2">
                <div className="h-4 bg-slate-100 rounded w-40" />
                <div className="h-3 bg-slate-100 rounded w-56" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <AlertCircle className="w-12 h-12 text-red-400 mb-4" />
        <h3 className="text-lg font-semibold text-slate-900 mb-2">Erreur</h3>
        <p className="text-slate-500 mb-6">{error}</p>
        <button onClick={fetchData} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-nexus-600 text-white text-sm font-medium hover:bg-nexus-700 transition-colors">
          <RefreshCw className="w-4 h-4" /> Reessayer
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Utilisateurs</h1>
          <p className="text-slate-500 text-sm mt-1">{filtered.length} utilisateur{filtered.length !== 1 ? 's' : ''}</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              placeholder="Rechercher..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 pr-4 py-2 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-nexus-500 focus:border-nexus-500 w-56"
            />
          </div>
          <div className="relative">
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              className="appearance-none pl-3 pr-8 py-2 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-nexus-500 focus:border-nexus-500 bg-white"
            >
              <option value="">Tous les roles</option>
              {NEXUS_ROLES.map((r) => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
            </select>
            <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
          </div>
          <button onClick={openAddModal} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-nexus-600 text-white text-sm font-medium hover:bg-nexus-700 transition-colors">
            <Plus className="w-4 h-4" /> Ajouter
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="text-left font-semibold text-slate-700 px-4 py-3">Nom</th>
                <th className="text-left font-semibold text-slate-700 px-4 py-3 hidden md:table-cell">Email</th>
                <th className="text-left font-semibold text-slate-700 px-4 py-3">Role Nexus</th>
                <th className="text-left font-semibold text-slate-700 px-4 py-3 hidden lg:table-cell">Roles Apps</th>
                <th className="text-left font-semibold text-slate-700 px-4 py-3">Statut</th>
                <th className="text-right font-semibold text-slate-700 px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map((u) => {
                const role = u.nexus_role || u.role || 'user';
                const status = u.status || (u.is_active === false ? 'inactive' : 'active');
                const roleBadge = ROLE_BADGE[role] || ROLE_BADGE.user;
                const statusBadge = STATUS_BADGE[status] || STATUS_BADGE.active;
                const initials = `${(u.prenom || u.first_name || '')[0] || ''}${(u.nom || u.last_name || '')[0] || ''}`.toUpperCase() || 'U';

                return (
                  <tr key={u.id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-nexus-100 flex items-center justify-center flex-shrink-0">
                          <span className="text-xs font-semibold text-nexus-700">{initials}</span>
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">
                            {u.prenom || u.first_name || ''} {u.nom || u.last_name || ''}
                          </p>
                          <p className="text-xs text-slate-500 md:hidden">{u.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-600 hidden md:table-cell">{u.email}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${roleBadge}`}>{role}</span>
                    </td>
                    <td className="px-4 py-3 hidden lg:table-cell">
                      <div className="flex flex-wrap gap-1">
                        {u.app_roles && Object.entries(u.app_roles).length > 0 ? (
                          Object.entries(u.app_roles).map(([app, appRole]) => (
                            <span key={app} className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded font-medium">
                              {app}: {typeof appRole === 'object' ? appRole?.role : appRole}
                            </span>
                          ))
                        ) : (
                          <span className="text-xs text-slate-400">--</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${statusBadge}`}>{status}</span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => openEditModal(u)}
                          className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors"
                          title="Modifier"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        {status !== 'inactive' && u.id !== currentUser?.id && (
                          <button
                            onClick={() => setConfirmDeactivate(u)}
                            className="p-1.5 rounded-lg text-slate-400 hover:bg-red-50 hover:text-red-600 transition-colors"
                            title="Desactiver"
                          >
                            <UserX className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center text-slate-500">
                    Aucun utilisateur trouve
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add/Edit Modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setModalOpen(false)} />
          <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-slate-200">
              <h3 className="text-lg font-semibold text-slate-900">
                {editingUser ? "Modifier l'utilisateur" : 'Nouvel utilisateur'}
              </h3>
              <button onClick={() => setModalOpen(false)} className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    type="email"
                    required
                    value={form.email}
                    onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
                    className="w-full pl-10 pr-4 py-2 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-nexus-500 focus:border-nexus-500"
                    placeholder="email@exemple.com"
                    disabled={!!editingUser}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Nom</label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                      type="text"
                      required
                      value={form.nom}
                      onChange={(e) => setForm((p) => ({ ...p, nom: e.target.value }))}
                      className="w-full pl-10 pr-4 py-2 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-nexus-500 focus:border-nexus-500"
                      placeholder="Dupont"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Prenom</label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                      type="text"
                      required
                      value={form.prenom}
                      onChange={(e) => setForm((p) => ({ ...p, prenom: e.target.value }))}
                      className="w-full pl-10 pr-4 py-2 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-nexus-500 focus:border-nexus-500"
                      placeholder="Jean"
                    />
                  </div>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Role Nexus-360</label>
                <div className="relative">
                  <Shield className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <select
                    value={form.nexus_role}
                    onChange={(e) => setForm((p) => ({ ...p, nexus_role: e.target.value }))}
                    className="appearance-none w-full pl-10 pr-8 py-2 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-nexus-500 focus:border-nexus-500 bg-white"
                  >
                    {NEXUS_ROLES.map((r) => (
                      <option key={r.value} value={r.value}>{r.label}</option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                </div>
              </div>

              {/* App Roles */}
              {apps.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Roles par application</label>
                  <div className="space-y-2">
                    {apps.filter((a) => a.status !== 'coming_soon').map((app) => (
                      <div key={app.code || app.id} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                        <AppWindow className="w-4 h-4 text-slate-400 flex-shrink-0" />
                        <span className="text-sm font-medium text-slate-700 w-24">{app.name || app.nom}</span>
                        <select
                          value={(typeof form.app_roles[app.code] === 'object' ? form.app_roles[app.code]?.role : form.app_roles[app.code]) || ''}
                          onChange={(e) => {
                            const val = e.target.value;
                            setForm((p) => {
                              const newRoles = { ...p.app_roles };
                              if (val) {
                                newRoles[app.code] = val;
                              } else {
                                delete newRoles[app.code];
                              }
                              return { ...p, app_roles: newRoles };
                            });
                          }}
                          className="flex-1 appearance-none px-3 py-1.5 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-nexus-500 focus:border-nexus-500 bg-white"
                        >
                          <option value="">Aucun acces</option>
                          <option value="admin">Admin</option>
                          <option value="manager">Manager</option>
                          <option value="user">Utilisateur</option>
                          <option value="viewer">Lecteur</option>
                        </select>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-200">
                <button type="button" onClick={() => setModalOpen(false)} className="px-4 py-2 rounded-lg border border-slate-300 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors">
                  Annuler
                </button>
                <button type="submit" disabled={saving} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-nexus-600 text-white text-sm font-medium hover:bg-nexus-700 disabled:opacity-50 transition-colors">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  {editingUser ? 'Mettre a jour' : 'Creer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Deactivate Confirmation */}
      {confirmDeactivate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setConfirmDeactivate(null)} />
          <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-sm p-6">
            <div className="flex flex-col items-center text-center">
              <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mb-4">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">Desactiver l'utilisateur ?</h3>
              <p className="text-sm text-slate-500 mb-6">
                {confirmDeactivate.prenom || confirmDeactivate.first_name} {confirmDeactivate.nom || confirmDeactivate.last_name} n'aura plus acces a la plateforme.
              </p>
              <div className="flex items-center gap-3 w-full">
                <button onClick={() => setConfirmDeactivate(null)} className="flex-1 px-4 py-2 rounded-lg border border-slate-300 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors">
                  Annuler
                </button>
                <button onClick={handleDeactivate} disabled={saving} className="flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700 disabled:opacity-50 transition-colors">
                  {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                  Desactiver
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;
